// Comprehensive Washington DC family law data based on Custody X Change and official DC sources
const washingtonDCData = {
  'washington-dc': {
    name: 'Washington DC',
    overview: {
      keyPoints: [
        'Washington DC follows the "best interests of the child" standard with specific factors outlined in DC Code § 16-914',
        'Joint custody is not presumed - courts make individualized determinations based on evidence and circumstances',
        'DC uses "custody" and "visitation" terminology rather than modern parenting time language',
        'The District requires detailed parenting plans in all contested custody cases involving minor children',
        'DC has a strong preference for maintaining meaningful relationships between children and both parents',
        'Relocation with children requires court approval or written consent from the other parent',
        'DC courts emphasize stability and continuity in the child\'s living arrangements'
      ],
      uniqueFeatures: [
        'Washington DC is a federal district with unique jurisdictional considerations',
        'The District has a unified family court system handling all family law matters',
        'DC allows for informal custody agreements that can be formalized by the court',
        'The District has specific provisions for federal employees and military families',
        'DC recognizes domestic partnerships with same rights as marriage for custody purposes',
        'The District has specialized domestic violence court with integrated family services',
        'Washington DC has mandatory mediation programs for custody disputes'
      ]
    },
    legalRequirements: {
      residency: 'Either parent or the child must have lived in Washington DC for at least 6 months before filing, or DC must be the child\'s home state under UCCJEA',
      filing: 'File in DC Superior Court Family Court, which has exclusive jurisdiction over family law matters in the District',
      paternity: 'Unmarried fathers can establish paternity through voluntary acknowledgment, genetic testing, court order, or presumption of paternity',
      documentation: 'Required documents include complaint, summons, financial statement, and proposed parenting plan in contested cases',
      uniqueRules: [
        'DC requires financial disclosure in all family law cases involving children or support',
        'Mediation is mandatory for all contested custody cases unless domestic violence is involved',
        'Guardian ad litem appointment is common in contested custody cases',
        'Parent education programs are required for divorcing parents with minor children',
        'DC allows emergency custody orders for situations involving child welfare'
      ]
    },
    custodyTypes: {
      physicalCustody: 'DC uses "physical custody" to determine where the child primarily resides and who provides day-to-day care',
      legalCustody: 'Legal custody involves decision-making authority for major life decisions including education, healthcare, and religious upbringing',
      jointCustody: 'Joint custody (both legal and physical) requires court approval and demonstration that it serves the child\'s best interests',
      visitation: 'Called "visitation rights" - includes specific schedules for the non-custodial parent\'s time with the child',
      specialProvisions: [
        'Supervised visitation may be ordered when child safety or welfare concerns exist',
        'Virtual visitation (video calls) is recognized for long-distance situations',
        'Extended summer visitation is standard to accommodate school schedules',
        'Holiday visitation typically alternates between parents on annual basis',
        'DC recognizes grandparent visitation rights under specific circumstances',
        'Third-party custody may be awarded to relatives in exceptional circumstances'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Detailed custody and visitation schedule including regular rotation, weekends, and holidays',
        'Specific holiday and vacation schedules with clear alternating arrangements between parents',
        'Transportation arrangements and responsibility for travel costs between parents\' residences',
        'Communication methods between parents and guidelines for parent-child contact during visitation',
        'Decision-making authority allocation for education, healthcare, religious, and extracurricular activities',
        'Dispute resolution procedures including mediation requirements before court intervention',
        'Relocation provisions and notification requirements for moves affecting the child\'s residence',
        'Any special conditions, restrictions, or accommodations for either parent',
        'Provisions for child care during work hours and right of first refusal',
        'Guidelines for introducing new romantic partners to the children',
        'Emergency contact procedures and decision-making protocols'
      ],
      tips: [
        'Be extremely specific about pickup and drop-off times, locations, and procedures to avoid confusion',
        'Include provisions for DC\'s urban environment and public transportation considerations',
        'Address how to handle missed visitation due to work schedules, traffic, or emergencies',
        'Include specific language about meeting locations and parking considerations in urban DC',
        'Specify how major decisions will be made and communicated between parents',
        'Address the child\'s participation in extracurricular activities and school events',
        'Include technology use guidelines and virtual visitation provisions',
        'Plan for federal holidays and DC-specific observances that may affect schedules'
      ]
    },
    childSupport: {
      guidelines: 'Washington DC uses a modified Income Shares Model based on both parents\' gross income with DC-specific cost-of-living adjustments',
      factors: [
        'Gross monthly income of both parents from all sources',
        'Number of children covered by the support order',
        'Cost of health insurance premiums for the children',
        'Childcare expenses necessary for employment or education',
        'Extraordinary medical expenses not covered by insurance',
        'Other court-ordered support obligations',
        'Visitation time and overnight stays with paying parent (if significant)',
        'Standard of living the child would have enjoyed if parents remained together',
        'Special needs of the child requiring additional financial support',
        'DC\'s high cost of living and housing costs'
      ],
      calculation: 'DC Child Support Guidelines using Income Shares Model with District-specific adjustments for high cost of living',
      modifications: 'Substantial change in circumstances required, typically 15% change in support amount or significant change in income or circumstances',
      enforcement: 'Strong enforcement through DC Office of the Attorney General Child Support Services Division including wage garnishment, asset seizure, tax intercept, and license suspension',
      detailedInfo: {
        basicCalculation: {
          description: 'Washington DC follows a modified Income Shares Model that estimates what parents would spend on their children if the family were intact, with adjustments for DC\'s high cost of living.',
          steps: [
            'Calculate each parent\'s gross monthly income from all verifiable sources',
            'Determine allowable deductions including taxes, mandatory retirement, and existing support orders',
            'Calculate adjusted gross monthly income for each parent after allowable deductions',
            'Add both parents\' adjusted gross incomes to determine combined monthly income',
            'Use DC Child Support Schedule to find basic support obligation for number of children',
            'Apply DC cost-of-living adjustment factor to basic obligation',
            'Allocate support obligation proportionally based on each parent\'s percentage of combined income',
            'Add costs for health insurance, childcare, and extraordinary medical expenses',
            'Apply any adjustments for extended visitation time or special circumstances',
            'Calculate final monthly support amount payable by obligor parent'
          ]
        },
        incomeTypes: {
          included: [
            'Wages, salary, commissions, bonuses, and overtime pay',
            'Federal government salary and benefits',
            'Self-employment income (gross receipts minus ordinary business expenses)',
            'Investment income, dividends, interest, and capital gains',
            'Rental property income (after reasonable expenses)',
            'Retirement benefits, pensions, and annuity payments',
            'Social Security benefits (including disability and survivor benefits)',
            'Unemployment compensation and workers\' compensation benefits',
            'Military pay including base pay, allowances, and special pay',
            'Spousal support or maintenance received from previous relationships',
            'Trust income and regularly received gifts or inheritances',
            'Royalties, patents, and intellectual property income',
            'Business income from partnerships, corporations, or LLCs'
          ],
          excluded: [
            'Means-tested public assistance (TANF, SNAP, WIC, etc.)',
            'Foster care payments received for non-biological children',
            'Child support received for other children not subject to current order',
            'One-time gifts or inheritances (unless creating ongoing income)',
            'Student loans, grants, and educational financial aid',
            'Supplemental Security Income (SSI)',
            'Income of new spouse or cohabitating partner',
            'Reimbursement for job-related expenses',
            'Income from illegal activities'
          ]
        },
        deductions: {
          mandatory: [
            'Federal income taxes based on appropriate filing status',
            'DC income taxes',
            'FICA taxes (Social Security and Medicare)',
            'Mandatory retirement contributions required by employer',
            'Union dues required as condition of employment',
            'Court-ordered child support for other children',
            'Court-ordered spousal support/maintenance to former spouse'
          ],
          optional: [
            'Health insurance premiums for the child (added separately to support)',
            'Childcare expenses necessary for employment or education',
            'Extraordinary medical expenses for the child (shared proportionally)'
          ]
        },
        visitationAdjustment: {
          description: 'DC allows adjustment to child support when the paying parent has significant overnight visitation time with the child.',
          thresholds: [
            'No adjustment for less than 25% overnight visitation (91 nights per year)',
            'Potential adjustment for 25-35% overnight visitation (91-127 nights per year)',
            'Standard adjustment consideration for 35%+ overnight visitation (128+ nights per year)',
            'Extended visitation adjustment may apply for equal or near-equal time arrangements'
          ],
          calculation: 'Adjustment reduces support obligation based on additional direct costs during extended visitation and reduced costs to custodial parent',
          factors: [
            'Actual overnight stays documented over previous 12 months',
            'Additional housing costs for accommodating child during visitation in expensive DC market',
            'Duplicate expenses (clothing, toys, school supplies) in both homes',
            'Transportation costs for visitation exchanges in urban environment',
            'Reduced household expenses for custodial parent during visitation periods'
          ]
        },
        additionalExpenses: {
          healthInsurance: {
            description: 'Cost of health insurance premiums specifically attributable to covering the child',
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            requirements: 'Must be reasonable in cost, accessible to child, and provide adequate coverage',
            considerations: [
              'Premium cost must be specifically for child coverage, not family plan differential',
              'Insurance must be available through employer or DC Health Link marketplace',
              'Coverage should include medical, dental, and vision if reasonably available',
              'Both parents should have access to insurance cards and provider information',
              'Special considerations for federal employee health benefits'
            ]
          },
          childcare: {
            description: 'Necessary childcare costs directly related to employment, job training, or education',
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            requirements: 'Must be work-related or education-related necessity for custodial parent',
            considerations: [
              'Childcare must be necessary for parent to work or attend school',
              'Costs should be reasonable for DC area and type of care',
              'Before and after school care is typically included',
              'Summer childcare during school breaks may be included',
              'Receipts and documentation required for verification',
              'DC\'s high childcare costs are factored into calculations'
            ]
          },
          extraordinaryMedical: {
            description: 'Uninsured medical, dental, and vision expenses exceeding $250 per year per child',
            examples: [
              'Dental treatment not covered by insurance (fillings, crowns, extractions)',
              'Orthodontic treatment and appliances',
              'Vision care including eye exams, glasses, and contact lenses',
              'Mental health counseling and therapy sessions',
              'Physical therapy and rehabilitation services',
              'Prescription medications not covered by insurance',
              'Medical equipment and supplies (braces, wheelchairs, etc.)',
              'Treatment for chronic conditions requiring ongoing care',
              'Emergency medical treatment and hospitalization',
              'Specialized medical care and consultations'
            ],
            allocation: 'Shared proportionally between parents based on their respective income percentages',
            procedure: 'Parent incurring expense should seek pre-approval for non-emergency expenses over $500'
          },
          extracurricular: {
            description: 'Costs for children\'s activities, sports, and enrichment programs',
            examples: [
              'Youth sports team fees, equipment, and travel costs',
              'Music lessons and instrument rental or purchase',
              'Dance, martial arts, gymnastics, or other activity classes',
              'Summer camps and recreational day programs',
              'School trips, field trips, and educational activities',
              'Tutoring and educational support services',
              'Art, theater, or other creative programs',
              'Religious education and confirmation programs'
            ],
            note: 'Not automatically included in basic support calculation - must be agreed upon by parents or specifically court-ordered',
            considerations: [
              'Activities should be in the child\'s best interest and age-appropriate',
              'Costs should be reasonable and within family\'s financial means',
              'Both parents should have input on major extracurricular decisions',
              'Transportation to activities should be considered in urban DC environment'
            ]
          }
        },
        modifications: {
          grounds: [
            'Substantial change in either parent\'s income (typically 15% or more)',
            'Significant change in visitation schedule or custody arrangement',
            'Change in health insurance costs or availability',
            'Change in childcare expenses or needs',
            'Child\'s needs change significantly (special needs, medical conditions)',
            'Emancipation, marriage, or death of a child',
            'Three years have passed since last order (allows for cost-of-living review)',
            'Change in number of children covered by order',
            'Job loss or change in employment circumstances'
          ],
          process: [
            'File Motion to Modify Child Support with DC Superior Court',
            'Serve the other parent with motion and financial statement',
            'Provide updated income verification and expense documentation',
            'Attend court hearing or mediation as scheduled by court',
            'Present evidence supporting the requested modification',
            'Obtain modified court order with new support amount'
          ],
          timeline: 'Modifications are typically effective from the date of filing the motion, not the date of the final court order',
          documentation: [
            'Updated financial statement with current income and expenses',
            'Recent pay stubs, tax returns, and employment verification',
            'Documentation of changed circumstances (medical bills, job loss, etc.)',
            'Current childcare and health insurance costs',
            'Evidence of visitation schedule changes if applicable'
          ]
        },
        enforcement: {
          methods: [
            'Income withholding and automatic wage garnishment',
            'Interception of tax refunds (federal and DC)',
            'Asset seizure including bank accounts and personal property',
            'Liens on real estate and personal property',
            'Suspension of driver\'s license and professional licenses',
            'Credit bureau reporting of delinquent support',
            'Contempt of court proceedings with possible jail time',
            'Passport denial for arrearages exceeding $2,500',
            'Interception of lottery winnings and gaming proceeds',
            'Garnishment of federal wages and benefits'
          ],
          interest: 'Unpaid child support accrues interest at the rate of 6% per annum on past-due amounts',
          statute: 'No statute of limitations exists for collecting past-due child support - it remains collectible indefinitely',
          resources: [
            'DC Office of the Attorney General Child Support Services Division',
            'DC Superior Court Family Court',
            'Federal Office of Child Support Enforcement',
            'Interstate enforcement through other jurisdictions'
          ]
        }
      }
    },
    resources: {
      courts: [
        {
          name: 'DC Superior Court Family Court',
          url: 'https://www.dccourts.gov/superior-court/family-court',
          description: 'Official DC Superior Court family division with forms, procedures, and self-help resources',
          address: '500 Indiana Avenue, NW, Washington, DC 20001',
          phone: '(202) 879-1010'
        },
        {
          name: 'DC Courts Self-Help Center',
          url: 'https://www.dccourts.gov/services/self-help-center',
          description: 'Self-help resources and assistance for people representing themselves in family court'
        },
        {
          name: 'DC Family Court Forms',
          url: 'https://www.dccourts.gov/superior-court/family-court/forms',
          description: 'Complete collection of DC family court forms for divorce, custody, and support cases'
        },
        {
          name: 'DC Domestic Violence Unit',
          url: 'https://www.dccourts.gov/superior-court/civil-division/domestic-violence-unit',
          description: 'Specialized court unit handling domestic violence cases with integrated family services'
        }
      ],
      support: [
        {
          name: 'DC Office of the Attorney General - Child Support Services',
          url: 'https://oag.dc.gov/service/child-support-services',
          phone: '(202) 442-9900',
          description: 'Official DC agency for child support establishment, modification, and enforcement'
        },
        {
          name: 'Legal Aid Society of DC',
          url: 'https://www.legalaiddc.org/',
          phone: '(202) 628-1161',
          description: 'Free legal services for low-income families in family law matters'
        },
        {
          name: 'DC Bar Pro Bono Center',
          url: 'https://www.dcbar.org/pro-bono',
          phone: '(202) 780-2564',
          description: 'Pro bono legal services and attorney referrals for family law cases'
        },
        {
          name: 'DC Coalition Against Domestic Violence',
          url: 'https://dccadv.org/',
          phone: '(202) 299-1181',
          description: 'District-wide domestic violence resources, shelters, and legal advocacy services'
        },
        {
          name: 'Children\'s Law Center',
          url: 'https://www.childrenslawcenter.org/',
          phone: '(202) 467-4900',
          description: 'Legal advocacy for children and families in DC'
        }
      ],
      legal: [
        {
          name: 'DC Bar Association Lawyer Referral Service',
          url: 'https://www.dcbar.org/for-the-public/lawyer-referral-service',
          phone: '(202) 780-2500',
          description: 'Professional attorney referrals for family law cases in Washington DC'
        },
        {
          name: 'George Washington University Law School Legal Clinic',
          url: 'https://www.law.gwu.edu/academics/clinics',
          description: 'Student-supervised legal services for qualifying family law matters'
        },
        {
          name: 'Georgetown University Law Center Family Justice Clinic',
          url: 'https://www.law.georgetown.edu/experiential-learning/clinics/',
          description: 'Clinical legal services including family law representation'
        },
        {
          name: 'DC Collaborative Law Group',
          url: 'https://www.collaborativelaw.org/find-a-professional/district-of-columbia/',
          description: 'Network of collaborative divorce attorneys and professionals in DC'
        }
      ],
      educational: [
        {
          name: 'Parents Apart Program',
          url: 'https://www.dccourts.gov/superior-court/family-court/parent-education',
          description: 'Mandatory parent education program for divorcing parents with minor children'
        },
        {
          name: 'DC Parenting Plan Resources',
          url: 'https://www.dccourts.gov/superior-court/family-court/parenting-plans',
          description: 'Comprehensive guide to creating effective parenting plans in Washington DC'
        },
        {
          name: 'DC Guardian ad Litem Program',
          url: 'https://www.dccourts.gov/superior-court/family-court/guardian-ad-litem',
          description: 'Information about guardian ad litem services for children in contested custody cases'
        },
        {
          name: 'DC Public Library Legal Resources',
          url: 'https://www.dclibrary.org/legalresources',
          description: 'Free access to legal databases and family law research materials'
        }
      ],
      filingInformation: {
        venues: [
          {
            name: 'DC Superior Court Family Court',
            address: '500 Indiana Avenue, NW, Washington, DC 20001',
            phone: '(202) 879-1010',
            jurisdiction: 'All family law matters in Washington DC including divorce, custody, and support'
          }
        ],
        filingFees: {
          'Divorce (with children)': '$80.00',
          'Divorce (without children)': '$80.00',
          'Custody/Visitation': '$45.00',
          'Child Support': '$45.00',
          'Modification': '$45.00',
          'Paternity': '$45.00',
          'Protection Order': 'No fee'
        },
        requiredForms: [
          'Complaint for Divorce or Custody',
          'Summons',
          'Financial Statement',
          'Parenting Plan (if children involved)',
          'Child Support Guidelines Worksheet',
          'Certificate of Service',
          'Final Decree or Order'
        ]
      },
      statutes: [
        {
          name: 'DC Code Title 16 - Particular Actions, Proceedings and Matters',
          url: 'https://code.dccouncil.us/dc/council/code/titles/16/',
          description: 'Primary statutes governing divorce, custody, and family law in Washington DC'
        },
        {
          name: 'DC Child Support Guidelines (DC Code § 16-916.01)',
          url: 'https://code.dccouncil.us/dc/council/code/sections/16-916.01.html',
          description: 'Official child support calculation guidelines and procedures'
        },
        {
          name: 'DC Uniform Child Custody Jurisdiction Act (DC Code § 16-4601.01 et seq.)',
          url: 'https://code.dccouncil.us/dc/council/code/titles/16/chapters/46/',
          description: 'Interstate custody jurisdiction and enforcement laws'
        }
      ],
      emergencyContacts: [
        {
          name: 'DC Domestic Violence Hotline',
          phone: '1-844-4HELPDC',
          description: '24/7 domestic violence crisis line and support services'
        },
        {
          name: 'DC Child and Family Services Agency Hotline',
          phone: '(202) 671-SAFE',
          description: '24/7 child abuse and neglect reporting hotline'
        },
        {
          name: 'DC Crisis & Emergency Services',
          phone: '(888) 793-4357',
          description: '24/7 mental health crisis intervention and counseling referrals'
        }
      ]
    },
    bestInterestsFactors: [
      'The wishes of the child\'s parent or parents as to custody',
      'The wishes of the child as to the custodian, if the child is of sufficient age and capacity to form an intelligent preference',
      'The interaction and interrelationship of the child with parents, siblings, and any other person who may significantly affect the child\'s best interest',
      'The child\'s adjustment to home, school, and community',
      'The mental and physical health of all individuals involved',
      'Evidence of an intrafamily offense',
      'The capacity of the parents to communicate and reach shared decisions affecting the child\'s welfare',
      'The willingness of the parents to share custody',
      'The prior involvement of each parent in the child\'s life',
      'The geographical proximity of the parental homes',
      'The demands of parental employment',
      'The age and number of children',
      'The sincerity of each parent\'s request',
      'The parent\'s ability to financially support a custody arrangement',
      'The impact on any benefits the child derives from government assistance'
    ],
    practicalTips: [
      'DC courts value detailed, specific parenting plans that minimize future disputes',
      'Document all interactions and communications with the other parent using email or text when possible',
      'Attend all required parent education programs - failure to complete can delay your case',
      'Be prepared for mandatory mediation - DC courts strongly encourage resolution outside of trial',
      'Keep detailed records of your time with your children and any expenses',
      'Follow all court orders precisely - violations can seriously impact future custody decisions',
      'Consider hiring a local attorney familiar with DC Superior Court judges and procedures',
      'Take advantage of DC\'s extensive public transportation when planning visitation schedules',
      'Be aware of federal holidays and DC-specific observances that may affect court schedules'
    ]
  }
};

export default washingtonDCData;